import {
  LanguageConfig,
  MessageBackendChannel,
  MessageChannel,
  MessagePushAndInboxSelector,
  MessageWhatsappTemplateType,
} from 'config/CustomEnums';
import { loading } from 'models/LoadingUtil';
import { getPushAndInboxSelector, parseChannel } from 'models/MessageListModel';
import {
  MESSAGE_PAGE_SIZE,
  getSystemMessage,
  getSystemMessages,
} from 'services/MessageApiHelper';

import {
  convertCursorToNumber,
  convertNumberToCursor,
  formatNumberWithCommas,
  getDisplayLastModifiedBy,
} from 'utils';
import {
  TimeFormater,
  formatDate,
  getDetailPageDisplayLastModifiedDate,
  getListDisplayLastModifiedDate,
} from 'utils/TimeFormatUtil';

const getInitialState = () => ({
  listDisplayFields: [
    { displayName: 'ID', fieldName: 'pk' },
    {
      displayName: 'Name',
      fieldName: 'name',
      linked: true,
      orderField: 'name',
    },
    { displayName: 'Message channel', fieldName: 'displayChannels' },
    { displayName: 'Last modified time', fieldName: 'displayLastModifiedTime' },
  ],
  currentPageMessageList: [],
  allMessageList: [],
  pageInfo: {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  currentLastCursor: '',
  currentPage: 0,
  totalPage: 0,
  totalCount: 0,
  checkedList: [],
  message: {},
  errorFields: [],
});

const parseMessageTranslations = (translations) => {
  let parsedTranslations = {};
  translations.forEach((item) => {
    const translationData = item.node;
    let language = translationData.language;
    if (language === 'ZH_HANS') {
      language = LanguageConfig.simplifiedChinese;
    } else if (language === 'ZH_HANT') {
      language = LanguageConfig.traditionalChinese;
    }
    parsedTranslations[language] = {
      name: translationData.title,
      content: translationData.content,
      photo: translationData.photo,
      id: translationData.id,
      pk: translationData.pk,
    };
  });
  return parsedTranslations;
};

const parseListMessage = (data) => {
  const parsedChannelList = data.channels.edges?.map((item) =>
    parseChannel(item.node.pk),
  );
  const translations =
    data.translations?.edges?.length > 0
      ? parseMessageTranslations(data.translations.edges)
      : {};
  return {
    pk: data.pk,
    id: data.id,
    name: data.name || '-',
    content: data.content,
    photo: data.photo,
    channels: parsedChannelList,
    displayChannels: parsedChannelList.toString(),
    relatedCampaign: {
      pk: data.campaign?.pk,
      name: data.campaign?.name,
    },
    relatedCampaignName: data.campaign?.pk
      ? `[ID: ${data.campaign?.pk}] ${data.campaign?.name}`
      : '-',
    translations: translations,
    displayLastModifiedTime: getListDisplayLastModifiedDate(
      data.lastModifiedDate,
    ),
  };
};

const parseImageUrl = (imageUrl) => {
  if (imageUrl !== null && imageUrl !== '') {
    return {
      type: 'TYPE_URL',
      value: imageUrl,
    };
  }
  return null;
};

export const parseDetailMessage = (data) => {
  console.log('parseDetailMessage:', data);
  if (!data) {
    return {};
  }
  const parsedChannelList = data?.channels?.edges?.map((item) =>
    parseChannel(item.node.pk),
  );
  const translations = {
    [LanguageConfig.english]: {
      name: data.title,
      content: data.content,
      photo: parseImageUrl(data.photo),
      emailTitle: data.emailTitle,
      emailContent: data.emailContent,
      smsContent: data.smsContent,
    },
  };
  const edges = data.translations?.edges || [];
  edges.forEach((translation) => {
    const {
      language,
      title,
      content,
      photo,
      emailTitle,
      emailContent,
      smsContent,
      pk,
      id,
    } = translation.node;
    translations[language] = {
      id: id,
      pk: pk,
      name: title,
      content: content,
      photo: parseImageUrl(photo),
      emailTitle: emailTitle,
      emailContent: emailContent,
      smsContent: smsContent,
    };
  });
  console.log('translations', translations);
  return {
    pk: data.pk,
    id: data.id,
    name: data.name || '',
    channels: parsedChannelList,
    pushAndInboxSelector: getPushAndInboxSelector(parsedChannelList), // for recurring message
    messageLandingPageToCampaign: data.messageLandingPageToCampaign, // for recurring message
    displayChannels: parsedChannelList.toString(),
    displayTemplateName: data.messageTemplate?.name || '-',
    displayIsStartWithTemplate: data.isStartWithTemplate ? 'Yes' : 'No',
    relatedCampaign: {
      pk: data.campaign?.pk,
      name: data.campaign?.name,
      isExclusive: data.campaign?.isExclusive,
    },
    translations,
    displayCreationDate: getDetailPageDisplayLastModifiedDate(
      data?.creationDate,
    ),
    displayLastModifiedDate: getDetailPageDisplayLastModifiedDate(
      data?.lastModifiedDate,
    ),
    displayLastModifiedBy: getDisplayLastModifiedBy(data),
  };
};

export default {
  namespace: 'systemMessageList',
  state: getInitialState(),
  reducers: {
    updateState(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    updateAllMessageList(state, { payload }) {
      return {
        ...state,
        allMessageList:
          payload?.page > 1
            ? [...state.allMessageList, ...payload?.currentPageMessageList]
            : payload?.currentPageMessageList,
      };
    },
    clearData(state, { payload }) {
      return {
        ...state,
        ...getInitialState(),
      };
    },
  },
  effects: {
    getCurrentPageMessages: [
      function* ({ payload }, { put }) {
        const { page, rank, searchKey, isAll } = payload;
        let afterCursor = '';
        if (page > 1) {
          afterCursor = convertNumberToCursor(
            (page - 1) * MESSAGE_PAGE_SIZE - 1,
          );
        }
        const serviceArgs = [
          getSystemMessages,
          afterCursor,
          rank,
          searchKey,
          payload,
        ];
        function* onSuccess(data) {
          const pageInfo = data.systemMessages.pageInfo;
          const totalCount = data.systemMessages.totalCount;
          const currentLastCursor = pageInfo.endCursor;
          const messageData = data.systemMessages.edges;
          const messageList = messageData.map((item) =>
            parseListMessage(item.node),
          );
          if (isAll) {
            yield put({
              type: 'updateAllMessageList',
              payload: {
                page,
                currentPageMessageList: messageList,
              },
            });
          } else {
            yield put({
              type: 'updateState',
              payload: {
                currentPageMessageList: messageList,
                pageInfo: {
                  startCursor: convertCursorToNumber(pageInfo?.startCursor) + 1,
                  endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
                },
                totalCount,
                currentLastCursor,
                totalPage: Math.ceil(totalCount / MESSAGE_PAGE_SIZE),
              },
            });
          }
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getMessage: [
      function* ({ payload }, { put }) {
        const { messagePK } = payload;
        const messageID = btoa(`SystemMessageNode:${messagePK}`);
        const serviceArgs = [getSystemMessage, messageID];
        function* onSuccess(data) {
          const messageData = data.systemMessage;
          const message = parseDetailMessage(messageData);
          yield put({
            type: 'updateState',
            payload: {
              message: message,
            },
          });
          const afterAction = payload.afterAction || (() => {});
          yield afterAction(message);
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    delete: [
      function* ({ payload }, { put, select }) {
        yield console.log('no delete');
      },
      { type: 'takeLatest' },
    ],
    duplicate: [
      function* ({ payload }, { put }) {
        yield console.log('no duplicate');
      },
      { type: 'takeLatest' },
    ],
  },
};
